import React, { useCallback, useEffect, useState } from 'react';
import { getMetaPlacements, importMetaPlacements } from '../api/appPacks';
import { isReportStale } from '../utils/metaPlacementReport';

// Config -> App Packs: import the Meta Monetization Manager placement CSV for one pack and show
// what is stored per platform. Capture-independent: it talks only to the pack's own routes.
// `nowMs` is the injectable clock for the stale note.
const TH = { textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--line)', fontWeight: 600 };
const TD = { padding: '4px 8px', borderBottom: '1px solid var(--line)', verticalAlign: 'top' };

export default function MetaPlacementReportPanel({ slug, nowMs, onImported }) {
  const [report, setReport] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!slug) { setReport({}); return; }
    try { setReport(await getMetaPlacements(slug)); } catch (e) { setMsg(`Could not load the report: ${e.message}`); }
  }, [slug]);

  useEffect(() => { setErr(null); setMsg(''); load(); }, [load]);

  const onFile = async (ev) => {
    const file = ev.target && ev.target.files && ev.target.files[0];
    if (!file || !slug) return;
    setBusy(true); setErr(null); setMsg('Importing…');
    try {
      const text = await file.text();
      const out = await importMetaPlacements(slug, text);
      const rej = out && out.rejectedRows ? ` (${out.rejectedRows} row(s) skipped: bad date)` : '';
      setMsg(`Imported ${((out && out.platforms) || []).join(', ') || 'report'}${rej}`);
      await load();
      if (typeof onImported === 'function') onImported();
    } catch (e) {
      setMsg('');
      setErr({ message: e.message, unmatched: e.unmatched || [], missing: e.missing || [] });
    } finally {
      setBusy(false);
      try { ev.target.value = ''; } catch { /* readonly in tests */ }
    }
  };

  const platforms = Object.keys(report || {});

  return (
    <div style={{ marginTop: 16, padding: 12, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 6 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Meta placement report</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
        Import the Monetization Manager placement CSV. The import is independent of the current capture;
        the placement checks that read it need a MITM capture (logcat rows do not carry the config or the responses).
      </div>
      {!slug && <div style={{ fontSize: 13 }}>Select an app pack first.</div>}
      <input type="file" accept=".csv,text/csv" disabled={!slug || busy} onChange={onFile} />
      {msg && <div style={{ fontSize: 12, marginTop: 6 }}>{msg}</div>}
      {err && (
        <div style={{ marginTop: 6, padding: 8, fontSize: 13, background: 'var(--error-bg)', border: '1px solid var(--line)' }}>
          Import rejected: {err.message}
          {err.unmatched.length > 0 && (
            <div>No Property in the CSV matched this pack. Unmatched Property names: {err.unmatched.join(', ')}</div>
          )}
          {err.missing.length > 0 && <div>Missing columns: {err.missing.join(', ')}</div>}
        </div>
      )}
      {slug && platforms.length === 0 && (
        <div style={{ fontSize: 13, marginTop: 8 }}>
          No Meta placement report imported. Import the Monetization Manager CSV above.
        </div>
      )}
      {platforms.map((platform) => {
        const entry = report[platform] || {};
        const rows = Array.isArray(entry.placements) ? entry.placements : [];
        const stale = isReportStale(entry, nowMs);
        return (
          <div key={platform} style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600 }}>{platform}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Imported {entry.importedAt ? String(entry.importedAt).slice(0, 10) : 'unknown'}
              {entry.window ? ` · window ${entry.window.from} to ${entry.window.to}` : ''} · {rows.length} placement(s)
            </div>
            {stale && (
              <div style={{ fontSize: 12, marginTop: 4, padding: 6, background: 'var(--warn-bg)', border: '1px solid var(--line)' }}>
                Report older than 30 days: dead and not-seen findings may be out of date.
              </div>
            )}
            <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                <thead>
                  <tr>
                    {['Placement ID', 'Name', 'Unit', 'Type', 'Size', 'Imps 7d', 'Imps 30d'].map((h) => <th key={h} style={TH}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.placementId}>
                      <td style={TD}>{p.placementId}</td>
                      <td style={TD}>{p.name}</td>
                      <td style={TD}>{p.derivedUnit || '-'}</td>
                      <td style={TD}>{p.derivedType || '-'}</td>
                      <td style={TD}>{p.derivedSize || '-'}</td>
                      <td style={TD}>{p.last7 ? p.last7.imps : 0}</td>
                      <td style={TD}>{p.last30 ? p.last30.imps : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
